import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Panel, SectionTitle, TactLoader } from "../components/Tactical";
import WhatsAppButton from "../components/WhatsAppButton";
import ScheduleBadge, { formatScheduleDate } from "../components/ScheduleBadge";
import { Swords } from "lucide-react";

export default function MyMatches() {
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/matches/my/all");
        if (alive) setMatches(data);
      } catch (e) { /* noop */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const myId = user && user !== false ? user.id : null;
  const pendingCount = matches.filter((m) => m.status !== "confirmed" && !m.admin_override).length;
  const completedCount = matches.length - pendingCount;

  return (
    <div data-testid="my-matches-page" className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <SectionTitle kicker="// player queue" testId="my-matches-title">My Games</SectionTitle>
        {matches.length > 0 && (
          <div className="flex gap-3 font-mono text-[10px] uppercase tracking-[0.3em]" data-testid="my-matches-summary">
            <span className="text-[#7C3AED]">{pendingCount} PENDING</span>
            <span className="text-[#71717A]">·</span>
            <span className="text-[#34C759]">{completedCount} COMPLETED</span>
          </div>
        )}
      </div>

      {loading ? <TactLoader /> : matches.length === 0 ? (
        <Panel className="font-mono text-[#71717A] text-sm" testId="my-matches-empty">
          [ NO GAMES YET. JOIN A LEAGUE TO GET FIXTURES. ]
        </Panel>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="my-matches-list">
          {matches.map((m) => (
            <MyMatchCard key={m.id} m={m} myId={myId} />
          ))}
        </div>
      )}
    </div>
  );
}

function MyMatchCard({ m, myId }) {
  const opponent = m.home_id === myId ? m.away : m.home;
  const opponentId = m.home_id === myId ? m.away_id : m.home_id;
  const homeIsMe = m.home_id === myId;
  const awayIsMe = m.away_id === myId;
  const finalized = m.status === "confirmed" || m.admin_override;
  const disputed = m.status === "disputed";

  // Figure out viewer-perspective outcome first — drives the left border color.
  let outcome = null; // "win" | "loss" | "draw" | null (not finalized)
  if (finalized) {
    const myScore = homeIsMe ? m.confirmed_score_home : m.confirmed_score_away;
    const oppScore = homeIsMe ? m.confirmed_score_away : m.confirmed_score_home;
    if (myScore > oppScore) outcome = "win";
    else if (myScore < oppScore) outcome = "loss";
    else outcome = "draw";
  }

  // Status-based left border: green WIN · red LOSS · yellow PENDING · red DISPUTED
  let leftBorderCls = "border-l-4 border-l-[#FFCC00]"; // pending default
  if (outcome === "win") leftBorderCls = "border-l-4 border-l-[#00FF87] shadow-[inset_4px_0_0_rgba(0,255,135,0.2)]";
  else if (outcome === "loss") leftBorderCls = "border-l-4 border-l-[#E11D48]";
  else if (outcome === "draw") leftBorderCls = "border-l-4 border-l-[#71717A]";
  else if (disputed) leftBorderCls = "border-l-4 border-l-[#E11D48]";

  const hoverBorder = finalized
    ? outcome === "win"
      ? "hover:border-[#00FF87]"
      : outcome === "loss" || disputed
        ? "hover:border-[#E11D48]"
        : "hover:border-[#71717A]"
    : "hover:border-[#FFCC00]";

  const statusLabel = finalized ? "COMPLETED" : m.status.toUpperCase();
  const statusColor = finalized
    ? outcome === "win" ? "text-[#00FF87]" : outcome === "loss" ? "text-[#E11D48]" : "text-[#71717A]"
    : disputed ? "text-[#E11D48]" : "text-[#FFCC00]";

  const resultChip = !finalized ? null
    : outcome === "win" ? { label: "WIN · +3", cls: "text-[#00FF87] border-[#00FF87]" }
    : outcome === "loss" ? { label: "LOSS", cls: "text-[#E11D48] border-[#E11D48]" }
    : { label: "DRAW · +1", cls: "text-[#71717A] border-[#D4D4D8] dark:border-[#3F3F46]" };

  const scoreStr = finalized ? `${m.confirmed_score_home} - ${m.confirmed_score_away}` : null;
  const scoreColor = outcome === "win"
    ? "text-[#00FF87] drop-shadow-[0_0_10px_rgba(0,255,135,0.35)]"
    : outcome === "loss"
      ? "text-[#E11D48]"
      : "text-[#71717A]";

  return (
    <div
      className={`bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] ${leftBorderCls} ${hoverBorder} match-card-lift`}
      data-testid={`my-match-${m.id}`}
    >
      <Link to={`/h2h/${opponentId}`} className="block p-5">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">
              RD {m.round}{m.scheduled_at ? ` · ${formatScheduleDate(m.scheduled_at).toUpperCase()}` : ""}
            </span>
            <ScheduleBadge match={m} />
          </div>
          <span className={`font-mono text-[10px] uppercase tracking-[0.3em] ${statusColor}`}>
            {statusLabel}{m.auto_drawn ? " · AUTO" : ""}
          </span>
        </div>

        {/* Players + big score. VS badge with swords icon between names when not finalized. */}
        <div className="flex items-center justify-between gap-3">
          <span className={`font-heading text-xl uppercase truncate flex-1 ${homeIsMe ? "text-[#00FF87]" : ""}`}>
            {m.home?.username || "—"}
          </span>

          {finalized ? (
            <span
              className={`font-score text-3xl sm:text-4xl leading-none px-3 ${scoreColor}`}
              data-testid={`my-match-score-${m.id}`}
            >
              {scoreStr}
            </span>
          ) : (
            <span
              className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#00FF87] text-white shadow-[0_0_16px_rgba(124,58,237,0.45)]"
              data-testid={`my-match-vs-${m.id}`}
              aria-label="VS"
            >
              <Swords className="w-4 h-4" strokeWidth={2.5} />
            </span>
          )}

          <span className={`font-heading text-xl uppercase truncate flex-1 text-right ${awayIsMe ? "text-[#00FF87]" : ""}`}>
            {m.away?.username || "—"}
          </span>
        </div>

        {resultChip && (
          <div className="mt-3 inline-flex">
            <span className={`font-mono text-[10px] uppercase tracking-[0.3em] border px-2 py-0.5 ${resultChip.cls}`} data-testid={`my-result-${m.id}`}>
              {resultChip.label}
            </span>
          </div>
        )}
      </Link>

      {opponent?.whatsapp && !finalized && (
        <div className="px-5 pb-4 pt-0 flex items-center justify-between gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">CONTACT {opponent.username}</span>
          <WhatsAppButton number={opponent.whatsapp} testId={`whatsapp-opponent-${m.id}`} size="sm" />
        </div>
      )}
    </div>
  );
}
